<script setup>
import { computed, ref } from 'vue'
import {formatPrice, formatNumber} from '@helpers/utils'

const emit = defineEmits(['update:purchaseType', 'update:selectedListingPlan'])

const props = defineProps({
  listing: {
    type: Object,
    required: true,
  },
  quantity: {
    type: Number,
    required: true,
  },
  purchaseType: {
    type: String,
    required: true,
  },
  selectedListingPlan: {
    type: Object,
    required: true,
    default() {
      return {}
    }
  },
  selectedProduct: {
    type: Object,
    required: true,
  },
})

const frequencyDropdown = ref(false)

function selectListingPlan(plan) {
  emit('update:selectedListingPlan', plan)
  frequencyDropdown.value = false
}

const subscriptionPercentSavings = computed(() => {
  const type = props.selectedListingPlan?.discount_type
  return Math.round(type === 'fixed_amount'
      ? (props.selectedListingPlan?.discount_amount / +props.selectedProduct.price) * 100
      : props.selectedListingPlan?.discount_amount)
})
const subscriptionDollarSavings = computed(() => {
  const type = props.selectedListingPlan?.discount_type
  return type === 'fixed_amount'
      ? props.selectedListingPlan?.discount_amount
      : props.selectedProduct.price * (props.selectedListingPlan?.discount_amount / 100)
})
const subscriptionPrice = computed(() => {
  return (props.selectedProduct.price - subscriptionDollarSavings.value) * props.quantity
})
const bulkPrice = computed(() => {
  const breaks = (props.listing?.breaks || [])
      .filter(({ quantity }) => quantity <= props.quantity)
      .sort((a, b) => (a.quantity > b.quantity ? -1 : 1))

  let discount = 1
  if (breaks.length) {
    discount = 1 - (breaks[0].discount_amount / 100)
  }

  return props.selectedProduct.price * discount * props.quantity
})
const compareAtPrice = computed(() => {
  return props.selectedProduct?.compare_at_price ? props.selectedProduct.compare_at_price * props.quantity : 0
})

const pieces = computed(() => {
  return props.selectedProduct?.stocks?.length === 1
      ? (props.selectedProduct?.stocks?.[0]?.pieces * props.selectedProduct?.stocks?.[0]?.pivot?.quantity)
      : 0
})
</script>

<template>
  <div class="frequency">
    <div class="frequency__tabs">
      <div class="frequency__tab frequency__tab--once" :class="{ 'frequency__tab--active': purchaseType === 'once' }">
        <div class="frequency__inner" @click="emit('update:purchaseType', 'once')">
          <div class="frequency__radio">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="11.25" fill="white" stroke="#183D4F" stroke-width="1.5"/>
              <circle cx="12" cy="12" r="6" fill="#183D4F" class="frequency__radio-fill" />
            </svg>
          </div>
          <div class="frequency__content">
            <span class="frequency__title">One-Time Purchase</span>
          </div>
          <div class="frequency__pricing">
            <span v-if="compareAtPrice" class="frequency__compare-at-price">${{ formatPrice(compareAtPrice, 2) }}</span>
            <span class="frequency__price">${{ formatPrice(bulkPrice, 2) }}<small v-if="pieces > 1">/{{ pieces }}ct</small></span>
          </div>
        </div>
      </div>
      <div v-if="listing.plans.length" class="frequency__tab frequency__tab--sub" :class="{ 'frequency__tab--active': purchaseType === 'sub' }">
        <div @click="emit('update:purchaseType', 'sub')">
          <div class="frequency__inner">
            <div class="frequency__radio">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="11.25" fill="white" stroke="#183D4F" stroke-width="1.5"/>
                <circle cx="12" cy="12" r="6" fill="#183D4F" class="frequency__radio-fill" />
              </svg>
            </div>
            <div class="frequency__content">
              <span class="frequency__title">
                Subscribe &amp; Save {{subscriptionPercentSavings}}%
              </span>
              <span class="frequency__tag">Most Popular</span>
            </div>
            <div class="frequency__pricing">
              <span class="frequency__price">${{ formatPrice(subscriptionPrice, 2) }}<small v-if="pieces > 1">/{{ pieces }}ct</small></span>
            </div>
          </div>
          <ul class="frequency__values">
            <li>Auto-shipped right to your door</li>
            <li>Cancel anytime</li>
          </ul>
        </div>
        <div class="frequency__delivery">
          <div class="frequency__selector" @click="frequencyDropdown = true">
            <div>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M1 1V7.875H1.79959M22.9149 10.625C22.2383 5.19882 17.6095 1 12 1C7.38361 1 3.4314 3.84373 1.79959 7.875M1.79959 7.875H7.875M23 23V16.125H22.2004M22.2004 16.125C20.5686 20.1563 16.6164 23 12 23C6.39053 23 1.76174 18.8012 1.0851 13.375M22.2004 16.125H16.125" stroke="white" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>Deliver Every</span>
            </div>
            <div>
              <span>{{ selectedListingPlan?.title?.replace('Every ', '') }}</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M3.41005 6.2101C3.95678 5.66337 4.84322 5.66337 5.38995 6.2101L10 10.8201L14.6101 6.2101C15.1568 5.66337 16.0432 5.66337 16.5899 6.2101C17.1367 6.75683 17.1367 7.64326 16.5899 8.19L10.9899 13.79C10.4432 14.3367 9.55678 14.3367 9.01005 13.79L3.41005 8.19C2.86332 7.64326 2.86332 6.75683 3.41005 6.2101Z" fill="white"/>
              </svg>
            </div>
          </div>

          <div v-if="frequencyDropdown" class="frequency__dropdown" v-click-outside="() => frequencyDropdown = false">
            <a class="frequency__option" href="#" v-for="plan in listing.plans" :key="plan.id" @click.prevent="selectListingPlan(plan)">
              <svg v-if="selectedListingPlan?.id === plan.id" class="frequency__icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 11L7 15L17 5" stroke="#3C3833" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <div v-else class="frequency__spacer"></div>
              {{ plan.title.replace('Every ', '') }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.frequency {
  margin-top: 16px;

  .frequency__tabs {
    .frequency__tab {
      border: 2px solid transparent;
      border-radius: 8px;
      background: var(--neutral-50, #F5F5F5);
      color: var(--neutral-1000, #3C3833);
      cursor: pointer;

      &:not(:last-of-type) {
        margin-bottom: 8px;
      }

      &.frequency__tab--sub {
        background: var(--Teal, #3B717B);
        color: var(--shades-white, #FFF);

        .frequency__inner {
          padding: 16px 16px 10px 16px;
        }
      }

      &.frequency__tab--active {
        border: 2px solid var(--Light-navy, #183D4F);

        .frequency__radio .frequency__radio-fill {
          fill: #183D4F;
        }
      }

      .frequency__inner {
        display: flex;
        align-items: flex-start;
        gap: 16px;
        padding: 16px;

        @media screen and (min-width: 768px) {
          align-items: center;
        }
      }

      .frequency__values {
        margin: 0;

        li {
          font-size: 12px;
          font-weight: 400;
          line-height: 22px;

          @media screen and (min-width: 768px) {
            font-size: 14px;
          }
        }
      }

      .frequency__radio {
        display: flex;

        .frequency__radio-fill {
          fill: #fff;
        }
      }

      .frequency__selector {
        display: flex;
        padding: 16px 16px;
        color: var(--shades-white, #FFF);
        font-size: 16px;
        font-weight: 600;
        line-height: 28px; /* 175% */
        gap: 16px;
        border-radius: 0 0 8px 8px;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        text-transform: capitalize;

        & > div:not(.frequency__dropdown) {
          display: flex;
          align-items: center;
          gap: 10px;

          @media screen and (min-width: 768px) {
            gap: 18px;
          }
        }
      }

      .frequency__delivery {
        position: relative;
      }

      .frequency__dropdown {
        position: absolute;
        background: var(--neutral-50, #F5F5F5);
        border-radius: 4px;
        bottom: -8px;
        right: 0;
        transform: translateY(100%);
        min-width: 160px;
        box-shadow: 0 1px 4px rgba(16, 24, 40, 0.20);

        .frequency__option {
          padding: 18px 16px;
          display: flex;
          align-items: center;
          text-decoration: none;
          font-weight: 400;
          gap: 14px;
          text-transform: capitalize;

          &:not(:last-of-type) {
            border-bottom: 1px solid #D8D7D6;
          }

          .frequency__icon,
          .frequency__spacer {
            width: 20px;
            height: 20px;
          }
        }
      }

      .frequency__content {
        flex-grow: 1;
        display: flex;
        align-items: flex-start;
        flex-direction: column;
        gap: 10px;

        @media screen and (min-width: 768px) {
          align-items: center;
          flex-direction: row;
        }

        .frequency__title {
          display: block;
          font-size: 16px;
          font-weight: 600;
          line-height: 20px;

          @media screen and (min-width: 768px) {
            font-size: 18px;
            line-height: 32px;
          }
        }
        .frequency__tag {
          border-radius: 8px;
          background: #183D4F;
          padding: 2px 8px;
          color: #FFF;
          font-family: Poppins, 'Poppins Fallback', 'Trebuchet MS Fallback', Arial, serif;
          font-size: 14px;
          font-weight: 600;
          line-height: 20px;

          @media screen and (min-width: 768px) {
            padding: 2px 12px;
            line-height: 24px; /* 171.429% */
          }
        }
        .frequency__description {
          display: block;
          color: var(--neutral-700, #767370);
          font-size: 12px;
          font-weight: 400;
          line-height: 20px;
          text-transform: capitalize;

          @media screen and (min-width: 768px) {
            font-size: 16px;
            line-height: 28px;
          }
        }
      }

      @media screen and (min-width: 768px) {
        &.frequency__tab--sub .frequency__pricing {
          display: flex;
          flex-direction: row-reverse;
          align-items: center;
          gap: 10px;
        }
      }

      &.frequency__tab--sub .frequency__pricing {
        .frequency__discount {
          color: var(--neutral-200, #D8D7D6);
        }
      }

      .frequency__pricing {
        text-align: right;
        display: flex;
        align-items: center;
        gap: 4px;

        @media screen and (min-width: 768px) {
          gap: 6px;
        }

        .frequency__compare-at-price {
          color: #B1AFAD;
          font-size: 14px;
          font-weight: 400;
          line-height: 18px; /* 128.571% */
          text-decoration: line-through;

          @media screen and (min-width: 768px) {
            font-size: 18px;
            line-height: 32px;
          }
        }
        .frequency__price {
          display: block;
          font-size: 16px;
          font-weight: 600;
          line-height: 20px;

          @media screen and (min-width: 768px) {
            font-size: 18px;
            line-height: 32px;
          }

          small {
            font-weight: 400;
          }
        }

        .frequency__discount {
          display: block;
          color: var(--primary-300, #E37F31);
          font-size: 14px;
          font-weight: 400;
          line-height: 20px;
          text-decoration: line-through;

          @media screen and (min-width: 768px) {
            font-size: 16px;
            line-height: 24px;
          }
        }
      }
    }
  }
}
</style>
